import { apiService } from './api';
import type { Resource, ResourceListParams, ResourceListResponse } from '../types';
import { logger } from '../utils/logger';

export class ResourceService {
  private baseUrl = '/api/resources';

  /**
   * Helper function to get authentication headers
   * Returns headers with x-user-id (from sessionStorage), Authorization (from localStorage), and x-school-code (from localStorage)
   */
  private getAuthHeaders(): HeadersInit {
    const headers: HeadersInit = {};

    // Add user ID from sessionStorage (required by backend)
    try {
      const userStr = sessionStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user && user.id) {
          headers['x-user-id'] = user.id;
        }
      }
    } catch (_) {
      // ignore
    }

    // Also add auth token as fallback (backend can use Authorization header as userId)
    const authToken = localStorage.getItem('authToken');
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    // Add schoolCode header for tenant context
    try {
      const schoolCode = localStorage.getItem('schoolCode');
      if (schoolCode && String(schoolCode).trim()) {
        headers['x-school-code'] = String(schoolCode).trim();
      }
    } catch (_) {
      // ignore
    }

    return headers;
  }

  async getResources(params: ResourceListParams = {}): Promise<ResourceListResponse> {
    const searchParams = new URLSearchParams();

    if (params.page) searchParams.append('page', params.page.toString());
    if (params.pageSize) searchParams.append('pageSize', params.pageSize.toString());
    if (params.searchTerm !== undefined && params.searchTerm !== null && params.searchTerm.trim() !== '') {
      searchParams.append('searchTerm', params.searchTerm.trim());
    }
    if (params.fileType) searchParams.append('fileType', params.fileType);
    if (params.tags) searchParams.append('tags', params.tags);
    if (params.tag) searchParams.append('tag', params.tag);
    if (params.categoryId) searchParams.append('categoryId', params.categoryId);
    if (params.excludeCategoryId) searchParams.append('excludeCategoryId', params.excludeCategoryId);
    if (params.startDate) searchParams.append('startDate', params.startDate);
    if (params.endDate) searchParams.append('endDate', params.endDate);

    const queryString = searchParams.toString();
    const endpoint = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;

    logger.log('🌐 resourceService.getResources - Endpoint:', endpoint);
    logger.log('🌐 resourceService.getResources - Params:', params);
    const responseData = await apiService.get<any>(endpoint);
    logger.log('✅ resourceService.getResources - Response:', responseData);

    // Extract resources array and pagination info from response
    const resources = responseData.resources || [];
    const totalCount = responseData.totalCount || 0;
    const page = responseData.page || 1;
    const pageSize = responseData.pageSize || 10;

    return { resources, totalCount, page, pageSize };
  }

  async getResourceById(id: string): Promise<Resource> {
    return apiService.get<Resource>(`${this.baseUrl}/${id}`);
  }

  async uploadResource(
    file: File,
    title: string,
    description?: string,
    tags: string[] = []
  ): Promise<Resource> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    if (description) {
      formData.append('description', description);
    }
    if (tags.length > 0) {
      formData.append('tags', tags.join(','));
    }

    // Get authentication headers (includes x-school-code)
    const headers = this.getAuthHeaders();

    // Get CSRF token from apiService
    let csrfToken = '';
    try {
      csrfToken = (apiService as any).csrfToken || '';
    } catch (_) {
      // ignore
    }

    // Add CSRF token for POST request
    const requestHeaders: HeadersInit = {
      ...headers,
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {})
    };

    // Use relative path (Vite proxy will handle it)
    const response = await fetch(`${this.baseUrl}/upload`, {
      method: 'POST',
      headers: requestHeaders,
      body: formData,
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      // Handle specific error cases with user-friendly messages
      if (response.status === 403) {
        const message = errorData.message || errorData.error || 'Bạn không có quyền tải lên tài nguyên. Vui lòng liên hệ quản trị viên.';
        throw new Error(message);
      }

      if (response.status === 400) {
        const message = errorData.message || errorData.error || 'Dữ liệu không hợp lệ. Vui lòng kiểm tra lại.';
        throw new Error(message);
      }

      if (response.status === 413) {
        throw new Error('File quá lớn. Kích thước file tối đa là 100MB.');
      }

      // Generic error message
      const message = errorData.message || errorData.error || `Lỗi khi tải lên tài nguyên (${response.status})`;
      throw new Error(message);
    }

    return await response.json();
  }

  async updateResource(id: string, resourceData: Partial<Resource>): Promise<Resource> {
    return apiService.put<Resource>(`${this.baseUrl}/${id}`, resourceData);
  }

  async deleteResource(id: string): Promise<void> {
    return apiService.delete<void>(`${this.baseUrl}/${id}`);
  }

  async deleteMultipleResources(ids: string[]): Promise<void> {
    // Get authentication headers
    const headers = this.getAuthHeaders();

    // Get CSRF token from apiService
    let csrfToken = '';
    try {
      csrfToken = (apiService as any).csrfToken || '';
    } catch (_) {
      // ignore
    }

    // Send IDs in request body for DELETE request
    const response = await fetch(`${this.baseUrl}/bulk`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...headers,
        ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {})
      },
      body: JSON.stringify({ ids }),
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: `HTTP error! status: ${response.status}` }));
      throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async getMyResources(page: number = 1, pageSize: number = 10): Promise<ResourceListResponse> {
    const searchParams = new URLSearchParams();
    searchParams.append('page', page.toString());
    searchParams.append('pageSize', pageSize.toString());

    const endpoint = `${this.baseUrl}/my-resources?${searchParams.toString()}`;

    const responseData = await apiService.get<any>(endpoint);

    // Extract resources array and pagination info from response
    const resources = responseData.resources || [];
    const totalCount = responseData.totalCount || 0;
    const pageNum = responseData.page || page;
    const pageSizeNum = responseData.pageSize || pageSize;

    return { resources, totalCount, page: pageNum, pageSize: pageSizeNum };
  }

  async viewResource(id: string): Promise<Resource> {
    return apiService.get<Resource>(`${this.baseUrl}/${id}/view`);
  }

  async downloadResource(id: string): Promise<Blob> {
    // Use relative path (Vite proxy will handle it)
    const headers = this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}/${id}/download`, {
      method: 'GET',
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.blob();
  }

  async getResourceFile(id: string): Promise<Blob> {
    // Get file content without incrementing download count (for viewing only)
    const headers = this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}/${id}/file`, {
      method: 'GET',
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('FILE_NOT_FOUND');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.blob();
  }

  // Get share token for a resource (authenticated)
  async getShareToken(id: string): Promise<{ token: string; resourceId: string }> {
    return apiService.get<{ token: string; resourceId: string }>(`${this.baseUrl}/${id}/share-token`);
  }

  // Public methods (no authentication required, but requires token)
  async getResourceByIdPublic(id: string, token: string): Promise<Resource> {
    // Get resource metadata (public endpoint with token)
    const response = await fetch(`${this.baseUrl}/public/${id}?token=${encodeURIComponent(token)}`, {
      method: 'GET',
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  async viewResourcePublic(id: string, token: string): Promise<void> {
    // Increment view count (public endpoint with token)
    const response = await fetch(`${this.baseUrl}/public/${id}/view?token=${encodeURIComponent(token)}`, {
      method: 'GET',
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  async getResourceFilePublic(id: string, token: string): Promise<Blob> {
    // Get file content without incrementing download count (public endpoint with token)
    const response = await fetch(`${this.baseUrl}/public/${id}/file?token=${encodeURIComponent(token)}`, {
      method: 'GET',
      credentials: 'include'
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('FILE_NOT_FOUND');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.blob();
  }

  async downloadResourcePublic(id: string, token: string): Promise<Blob> {
    // Download resource (public endpoint with token, increments download count)
    const response = await fetch(`${this.baseUrl}/public/${id}/download?token=${encodeURIComponent(token)}`, {
      method: 'GET',
      credentials: 'include'
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('FILE_NOT_FOUND');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.blob();
  }

  async reportFileError(resourceId: string, reason: string): Promise<void> {
    // Report file error to admin
    const headers = this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}/${resourceId}/report-error`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...headers
      },
      credentials: 'include',
      body: JSON.stringify({ reason })
    });

    if (!response.ok) {
      throw new Error('Không thể gửi báo cáo. Vui lòng thử lại sau.');
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileTypeIcon(fileType: string): string {
    switch (fileType.toLowerCase()) {
      case '.pdf':
        return '📄';
      case '.docx':
      case '.doc':
        return '📝';
      default:
        return '📁';
    }
  }

  isImageFile(fileType: string): boolean {
    const imageTypes = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    return imageTypes.includes(fileType.toLowerCase());
  }

  isVideoFile(fileType: string): boolean {
    const videoTypes = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm'];
    return videoTypes.includes(fileType.toLowerCase());
  }

  isAudioFile(fileType: string): boolean {
    const audioTypes = ['.mp3', '.wav', '.flac', '.aac', '.ogg'];
    return audioTypes.includes(fileType.toLowerCase());
  }

  async exportResources(dateFrom?: Date | null, dateTo?: Date | null): Promise<Resource[]> {
    const searchParams = new URLSearchParams();
    if (dateFrom) {
      searchParams.append('dateFrom', dateFrom.toISOString().split('T')[0]);
    }
    if (dateTo) {
      searchParams.append('dateTo', dateTo.toISOString().split('T')[0]);
    }

    const queryString = searchParams.toString();
    const endpoint = queryString ? `${this.baseUrl}/export?${queryString}` : `${this.baseUrl}/export`;

    const response = await apiService.get(endpoint);
    return Array.isArray(response) ? response : [];
  }
}

export const resourceService = new ResourceService();
export default resourceService;
